import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, ComponentSync } from '@nu-art/thunderstorm-frontend';
import './Component_PrettyPrint_Term.scss';
import { cloneObj, compare, exists, ThisShouldNotHappenException, voidFunction } from '@nu-art/ts-common';
import { DBDef_Expression, DBDef_Variable, LogicType_Add, LogicType_And, LogicType_Complex, LogicType_GreaterOrEqual, LogicType_Or, LogicType_StructuredFreeText } from '@app/hcs-shared';
import { Component_PrettyPrint_Atom } from '../Component_PrettyPrint_Atom/Component_PrettyPrint_Atom.js';
import { Component_PrettyPrint_ComplexTerm } from '../Component_PrettyPrint_ComplexTerm/Component_PrettyPrint_ComplexTerm.js';
import { PPDescription, PPIndicatorStatus } from '../Component_PrettyPrint_Description/Component_PrettyPrint_Description.js';
export class Component_PrettyPrint_Term extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(props, state) {
        state.term = props.term ? cloneObj(props.term) : undefined;
        state.termIndex = props.termIndex;
        state.titleOverwrite = props.titleOverwrite;
        state.atomToVarMap = props.atomToVarMap;
        //Interactiveness
        state.interactive = props.interactive;
        if ('interactiveProperties' in props) {
            state.calculationResult = props.interactiveProperties.calculationResult;
            state.actualValueMap = props.interactiveProperties.actualValueMap;
            state.suggestedValueMap = props.interactiveProperties.suggestedValueMap;
            state.selectedVariableId = props.interactiveProperties.selectedVariableId;
            state.partialValueMap = props.interactiveProperties.partialValueMap;
        }
        else {
            delete state.calculationResult;
            delete state.actualValueMap;
            delete state.suggestedValueMap;
            delete state.selectedVariableId;
        }
        return state;
    }
    shouldReDeriveState(nextProps) {
        return super.shouldReDeriveState(nextProps)
            || !compare(nextProps.term, this.state?.term);
    }
    //######################### Logic #########################
    getTermTitle = (operator, termValue, termFallbackValue) => {
        const withValues = exists(termValue) || exists(termFallbackValue);
        const value = exists(termValue)
            ? termValue[0] === termValue[1]
                ? termValue[0]
                : `[${termValue[0]},${termValue[1]}]`
            : '[Missing term return value]';
        const fallback = exists(termFallbackValue)
            ? termFallbackValue[0] === termFallbackValue[1]
                ? termFallbackValue[0]
                : `[${termFallbackValue[0]},${termFallbackValue[1]}]`
            : '[Missing term fallback value]';
        switch (operator) {
            case LogicType_And:
                return withValues
                    ? `If all of the following pass, returns ${value}, otherwise ${fallback}`
                    : 'All of:';
            case LogicType_Or:
                return withValues
                    ? `If one of the following passes, returns ${value}, otherwise ${fallback}`
                    : 'Any of:';
            case LogicType_Add:
                //Unique case - "Sum" already returns a number, no need for term value
                return 'Sum of:';
            case LogicType_GreaterOrEqual:
                //Unique case - this atom logic does not care about the fallback value, term value must exist
                return `Sum of the following is at least: ${termValue?.[0] ?? 0}`;
            default:
                return `Unhandled term atom logic: ${operator ?? 'undefined'}`;
        }
    };
    getTermStatus = () => {
        if (!this.state.interactive)
            return;
        const result = this.state.calculationResult?.['T'];
        switch (typeof result) {
            case 'number':
                return PPIndicatorStatus.pass;
            case 'boolean':
                return result ? PPIndicatorStatus.pass : PPIndicatorStatus.fail;
            default:
                return PPIndicatorStatus.dataNeeded;
        }
    };
    getAtomResolvingId = (atom) => {
        switch (atom.leftHand?.type) {
            case 'variable':
                return atom.leftHand.id;
            case 'expression': {
                const expression = this.props.modules.expression.cache.unique(atom.leftHand.id);
                return expression?.associatedVar;
            }
            case 'atom': {
                const id = Number(atom.leftHand.id) - 1;
                const mappedVar = this.state.atomToVarMap?.[id];
                switch (mappedVar?.type) {
                    case DBDef_Variable.dbKey:
                        return mappedVar.id;
                    case DBDef_Expression.dbKey: {
                        const expression = this.props.modules.expression.cache.unique(mappedVar.id);
                        return expression?.associatedVar;
                    }
                }
            }
        }
    };
    getAtomActualValue = (atom) => {
        if (!this.state.actualValueMap)
            return;
        const resolvingId = this.getAtomResolvingId(atom);
        if (!resolvingId)
            return;
        return this.state.actualValueMap[resolvingId];
    };
    getAtomSuggestedValue = (atom) => {
        if (!this.state.suggestedValueMap)
            return;
        const resolvingId = this.getAtomResolvingId(atom);
        if (!resolvingId)
            return;
        return this.state.suggestedValueMap[resolvingId];
    };
    getAtomPartialValue = (atom) => {
        if (!this.state.partialValueMap)
            return;
        const resolvingId = this.getAtomResolvingId(atom);
        return exists(resolvingId) ? this.state.partialValueMap[resolvingId] : undefined;
    };
    printTermInfo = (e) => {
        if (!e.shiftKey || !e.metaKey)
            return;
        this.logInfo(`T${this.state.termIndex} Info`, 'Calculation Result', this.state.calculationResult, 'Actual Value Map', this.state.actualValueMap, 'Suggested Value Map', this.state.suggestedValueMap);
    };
    //######################### Render #########################
    render() {
        if (!this.state.term)
            return this.render_TermNotFound();
        return this.render_Term(this.state.term);
    }
    render_TermNotFound = () => {
        return _jsxs("div", { className: 'term-pretty-print__not-found', children: ["T", this.state.termIndex, ": Term not found!"] });
    };
    render_Term = (term) => {
        //Term is not rendered without at least 1 atom
        if (!term.atoms.length)
            return _jsxs("div", { children: ["T", this.state.termIndex, ": Empty Term!"] });
        //Renders "free-text" term regardless of atom count
        if (term.atomsLogic === LogicType_StructuredFreeText)
            throw new ThisShouldNotHappenException('Component_PrettyPrint_Term is not supposed to show "LogicType_StructuredFreeText" terms');
        //Renders "complex" term regardless of atom count
        if (term.atomsLogic === LogicType_Complex)
            return _jsx(Component_PrettyPrint_ComplexTerm, { modules: this.props.modules, term: term, termIndex: this.state.termIndex, interactive: this.state.interactive });
        const termStatus = this.getTermStatus();
        //Renders a single atom term
        if (term.atoms.length === 1 && !exists(term.value))
            return this.render_Term_SingleAtom(term);
        if (term.atoms.length === 1 && !term.atomsLogic)
            term.atomsLogic = LogicType_And; //Single atom terms implied logic is "&&"
        //Renders multi-atom term based on atom-logic
        switch (term.atomsLogic) {
            case LogicType_And:
            case LogicType_Or:
            case LogicType_GreaterOrEqual:
            case LogicType_Add:
                return _jsxs(PPDescription, { className: _className('term-description', termStatus), children: [this.render_TermTitle(term, termStatus), ...this.render_TermAtoms(term.atoms)] });
            default:
                return _jsxs("div", { children: ["T", this.state.termIndex, ": Unhandled term atom logic ", term.atomsLogic ?? 'undefined'] });
        }
    };
    //######################### Render - Term Parts #########################
    render_TermTitle = (term, status) => {
        const title = this.state.titleOverwrite ?? this.getTermTitle(term.atomsLogic, term.value, term.fallbackValue);
        return _jsxs(PPDescription.Row, { onClick: this.printTermInfo, children: [_jsx(PPDescription.Indicator, { status: status }), _jsx(PPDescription.Title, { children: title })] });
    };
    render_TermAtoms = (atoms) => {
        const atomElements = [];
        const onVariableSelected = 'interactiveProperties' in this.props ? this.props.interactiveProperties.onVariableSelected : voidFunction;
        atoms.forEach((atom, index) => {
            const atomResult = this.state.calculationResult?.[`A${index + 1}`];
            const actualValue = this.getAtomActualValue(atom);
            const suggestedValue = this.getAtomSuggestedValue(atom);
            const partialValue = this.getAtomPartialValue(atom);
            atomElements.push(_jsxs(PPDescription.Row, { children: [_jsx(PPDescription.TreeLineConnector, {}), _jsx(Component_PrettyPrint_Atom, { modules: this.props.modules, atom: atom, atomIndex: index, atomToVarMap: this.state.atomToVarMap, interactive: this.state.interactive, interactiveProperties: {
                            calculationResult: atomResult,
                            actualValue: actualValue,
                            suggestedValue: suggestedValue,
                            selectedVariableId: this.state.selectedVariableId,
                            onVariableSelected: onVariableSelected,
                            partialValue: partialValue
                        } })] }));
        });
        return atomElements;
    };
    //######################### Render - Term Variants #########################
    render_Term_SingleAtom = (term) => {
        const atom = term.atoms[0];
        const atomResult = this.state.calculationResult?.['A1'];
        const actualValue = this.getAtomActualValue(atom);
        const suggestedValue = this.getAtomSuggestedValue(atom);
        const partialValue = this.getAtomPartialValue(atom);
        const onVariableSelected = 'interactiveProperties' in this.props ? this.props.interactiveProperties.onVariableSelected : voidFunction;
        return _jsx(Component_PrettyPrint_Atom, { modules: this.props.modules, atom: atom, atomIndex: 0, atomToVarMap: this.state.atomToVarMap, interactive: this.state.interactive, interactiveProperties: {
                calculationResult: atomResult,
                actualValue: actualValue,
                suggestedValue: suggestedValue,
                selectedVariableId: this.state.selectedVariableId,
                onVariableSelected: onVariableSelected,
                partialValue: partialValue,
            } });
    };
}
//# sourceMappingURL=Component_PrettyPrint_Term.js.map